import { useParams } from 'react-router-dom';
import { download, setAutoDownloadFlag } from '../../utility/api';
import { usePavilion } from '../contexts/PavilionContext';
import { useEffect } from 'react';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import { isMobileDevice, isNonWindowsOS } from '../../utility/utility';

const structuredData = {
  '@context': 'http://schema.org',
  '@type': 'Organization',
  name: 'Playcast',
  url: 'https://playcast.io',
  logo: 'https://playcast.io/images/playcast_logo-96x96.png',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+1-800-555-5555',
      contactType: 'Customer Service',
      areaServed: 'US',
      availableLanguage: 'English',
    },
  ],
  sameAs: [
    'https://discord.gg/7PYGxU68WH',
    'https://www.youtube.com/channel/UC-k16u9Ea_dvRhB70f-h5lQ',
    'https://www.twitch.tv/playcastio',
    'https://www.tiktok.com/@playcastdotio',
    'https://www.instagram.com/playcastio/',
  ],
};

export const SponsoredAffiliateLanding = () => {
  const { affiliate } = useParams();
  const { isPavilionMode } = usePavilion();
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <>
      <Helmet>
        <title>Playcast - Interactive Game Streaming with Peer-to-Peer Connectivity</title>
        <meta
          name='description'
          content='Playcast offers interactive peer-to-peer game streaming, enabling real-time, low-latency gameplay. Join our community and experience seamless gaming connections.'
        />
        <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className='flex w-full flex-col'>
        {/* Columns */}
        <div className="w-full bg-[#2C354C] bg-[url('/Gibber-Texture.png')] bg-[length:100%_auto] bg-bottom bg-no-repeat">
          <div className='container mx-auto flex flex-col gap-16 px-4 pb-12 pt-4 md:flex-row md:px-8 md:pt-0'>
            {/* Left Column */}
            <div className='flex w-full flex-col items-center pt-10 md:w-1/2'>
              <img src='/NewLogo.svg' alt='icon' className='w-full max-w-[346px] drop-shadow-xl' />
              <img src='/Playcast-title.svg' alt='Playcast title' className='w-full max-w-[461px] drop-shadow-xl' />
              <img src='/SteamCardBanner.svg' alt='Steam Card Banner' className='mt-20 h-[140px] w-full max-w-[720px] drop-shadow-xl' />
            </div>

            {/* Right Column */}
            <div className='flex w-full flex-col items-center justify-center gap-6 md:w-1/2'>
              <img src='/LevelUpSteamCard.svg' alt='Playcast title' className='h-auto w-4/5 max-w-[700px]' />
              <div className='font-inter w-full max-w-[700px] text-left text-white'>
                <p className='text-[20px] font-normal italic sm:text-[22px] md:text-[24px] lg:text-[25px]'>Hey there, gamer!</p>
                <p className='text-[20px] font-normal italic sm:text-[22px] md:text-[24px] lg:text-[25px]'>
                  <span className='text-[24px] font-bold not-italic sm:text-[26px] md:text-[28px] lg:text-[30px]'>{affiliate}</span> just
                  unlocked an epic way for you to power up their community—and earn a $10 Steam Gift Card while you're at it.
                </p>
              </div>
              <SignedIn>
                <img
                  src='/download-button.svg'
                  alt='download button'
                  className='mx-auto h-auto w-[40%] max-w-[400px] cursor-pointer'
                  onClick={() => download()}
                />
              </SignedIn>
              <SignedOut>
                <SignInButton mode='modal'>
                  <img
                    src='/download-button.svg'
                    alt='download button'
                    className='mx-auto h-auto w-[40%] max-w-[400px] cursor-pointer'
                    onClick={() => setAutoDownloadFlag(isMobileDevice() || isNonWindowsOS())}
                  />
                </SignInButton>
              </SignedOut>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className='w-full bg-[#1F273A] px-4 py-20 shadow-md md:px-20'>
          <div className='mb-12 text-center'>
            <h2 className='font-syne text-[40px] font-bold text-[#FFFEFA] sm:text-[50px] md:text-[60px]'>How It Works in</h2>
            <h2 className='font-syne text-[40px] font-bold text-[#FFD219] sm:text-[50px] md:text-[60px]'>3 Easy Steps</h2>
          </div>

          <div className='flex flex-wrap justify-center gap-12 xl:gap-20'>
            {/* Step 1 */}
            <div className='flex w-full flex-col items-center gap-6 sm:w-1/2 md:w-[33%] xl:w-[25%]'>
              <div className='font-syne text-center text-[26px] font-bold text-[#FFFEFA] sm:text-[32px] md:text-[36px] lg:text-[40px]'>
                <span className='text-[#FFD219]'>1.</span> Sign Up
              </div>
              <img
                src='SignUpIcon.svg'
                alt='Sign Up'
                className='h-auto w-full max-w-[200px] sm:max-w-[220px] md:max-w-[240px] lg:max-w-[260px]'
              />
              <p className='font-inter text-center text-[18px] italic text-[#FFFEFA] sm:text-[20px] md:text-[22px] lg:text-[25px]'>
                Create your free Playcast account in under a minute.
              </p>
            </div>

            {/* Step 2 */}
            <div className='flex w-full flex-col items-center gap-6 sm:w-1/2 md:w-[33%] xl:w-[25%]'>
              <div className='font-syne text-center text-[26px] font-bold text-[#FFFEFA] sm:text-[32px] md:text-[36px] lg:text-[40px]'>
                <span className='text-[#FFD219]'>2.</span> Install the Host App
              </div>
              <img
                src='InstallIcon.svg'
                alt='Install'
                className='h-auto w-full max-w-[200px] sm:max-w-[220px] md:max-w-[240px] lg:max-w-[260px]'
              />
              <p className='font-inter text-center text-[18px] italic text-[#FFFEFA] sm:text-[20px] md:text-[22px] lg:text-[25px]'>
                Download and Install the Playcast App using the button above.
              </p>
              <SignedIn>
                <img
                  src='/download-button.svg'
                  alt='download button'
                  onClick={() => download()}
                  className='h-auto w-4/5 max-w-[400px] cursor-pointer'
                />
              </SignedIn>
              <SignedOut>
                <SignInButton mode='modal'>
                  <img
                    src='/download-button.svg'
                    alt='download button'
                    onClick={() => setAutoDownloadFlag(isMobileDevice() || isNonWindowsOS())}
                    className='h-auto w-4/5 max-w-[400px] cursor-pointer'
                  />
                </SignInButton>
              </SignedOut>
            </div>

            {/* Step 3 */}
            <div className='flex w-full flex-col items-center gap-6 sm:w-1/2 md:w-[33%] xl:w-[25%]'>
              <div className='font-syne text-center text-[26px] font-bold text-[#FFFEFA] sm:text-[32px] md:text-[36px] lg:text-[40px]'>
                <span className='text-[#FFD219]'>3.</span> Stream & Earn*
              </div>
              <img
                src='GoLiveIcon.svg'
                alt='Go Live'
                className='h-auto w-full max-w-[200px] sm:max-w-[220px] md:max-w-[240px] lg:max-w-[260px]'
              />
              <p className='font-inter max-w-[392px] text-center text-[18px] italic text-[#FFFEFA] sm:text-[20px] md:text-[22px] lg:text-[25px]'>
                Stream any of your favorite games, share the link with a friend and have them join the game!
              </p>
            </div>
          </div>
        </div>

        {/* Terms & Conditions Section */}
        <div className='flex w-full items-center justify-center bg-[#2C354C] px-4 py-12 md:px-20'>
          <p className='font-inter max-w-5xl text-center text-[16px] font-normal leading-relaxed tracking-[0.8px] text-white text-opacity-60'>
            *To be eligible, participant must download the Playcast application via a designated affiliate link, register for a Playcast
            account, enable two-factor authentication (2FA), and host a qualifying session joined by at least one (1) additional device.
            Limit one (1) gift card per person. Gift cards are non-transferable, redeemable only via U.S. Steam accounts, and will be
            fulfilled within thirty (30) days of confirmed eligibility. Playcast reserves the right to withhold or revoke rewards in cases
            of suspected fraud, abuse, or circumvention of promotional terms. Offer valid only to individuals age 18 or older. Offer void
            where prohibited. Additional terms and eligibility requirements may apply.
          </p>
        </div>
      </div>
    </>
  );
};
